/**
 * How the items of one business system account are shown together.
 *
 * The server numbers the items of one account alike (`accountGroup`) and names the account
 * (`accountLabel`); the numbers mean nothing outside the response they came in, so grouping
 * only ever happens within one list. The cart slider groups within each payment and the
 * receipt within each payment card, both from here, so the two cannot drift apart.
 *
 * Only an account the user has linked is headed: it goes by the name they know it by. The items
 * of any other account gather with those of no account, so they are never told apart.
 */

/** The little of a cart item the grouping depends on. Structural, to avoid importing the
 *  generated client into a helper its consumers already import it from. */
export interface AccountGroupedItem {
    accountGroup?: number;
    accountLabel?: string;
    accountAlreadyLinked?: boolean;
}

export interface AccountItemGroup<T extends AccountGroupedItem = AccountGroupedItem> {
    /** The server's group number, or null for the items of no linked account. */
    accountGroup: number | null;

    /** What to head the group with, or null when there is nothing to head it with. */
    label: string | null;

    items: T[];
}

/** The group a linked account's items gather in, started the first time the account is seen. */
function accountGroupFor<T extends AccountGroupedItem>(
    started: Map<number, AccountItemGroup<T>>,
    accountGroup: number,
    accountLabel: string
): AccountItemGroup<T> {
    const already = started.get(accountGroup);

    if (already) {
        return already;
    }

    const group: AccountItemGroup<T> = {
        accountGroup,
        label: accountLabel,
        items: []
    };

    started.set(accountGroup, group);

    return group;
}

/**
 * The items gathered by linked account, accounts in the order they first appear, followed by one
 * unlabelled group holding the items of no linked account, if there are any.
 */
export function groupItemsByAccount<T extends AccountGroupedItem>(
    items: readonly T[] | null | undefined
): AccountItemGroup<T>[] {
    const byAccount = new Map<number, AccountItemGroup<T>>();
    const ungrouped: AccountItemGroup<T> = {
        accountGroup: null,
        label: null,
        items: []
    };

    for (const item of items ?? []) {
        // Groups are numbered from one, so no group means the business system attributed the
        // item to no account. An account the user has not linked has no name of theirs to head
        // its items with, and one with no label has nothing to head them with at all.
        const group = item.accountGroup && item.accountAlreadyLinked && item.accountLabel
            ? accountGroupFor(byAccount, item.accountGroup, item.accountLabel)
            : ungrouped;

        group.items.push(item);
    }

    const groups = Array.from(byAccount.values());

    return ungrouped.items.length ? [...groups, ungrouped] : groups;
}

/**
 * Whether the groups are worth heading: when any of them is a linked account's, which goes by
 * the name the user knows it by, so the heading says where the items came from. Otherwise every
 * item sits in the one group of no linked account, and a heading over everything would say
 * nothing - the flat list it always was.
 */
export function hasAccountGroupHeadings(groups: readonly AccountItemGroup[]): boolean {
    return groups.some(group => group.label !== null);
}
